import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import i18next from 'i18next';
import { initReactI18next, I18nextProvider } from 'react-i18next';
import {
    languageLabels,
    supportedLanguages,
    type Language,
} from './translations';

type TranslationVariables = Record<string, string | number>;

type LanguageContextValue = {
    language: Language;
    setLanguage: (language: Language) => void;
    t: (key: string, variables?: TranslationVariables) => string;
    languageOptions: { value: Language; label: string }[];
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

const storageKey = 'preferred-language';

const localeModules = import.meta.glob('./locale/*.json', { eager: true });
const translations = supportedLanguages.reduce<Record<Language, Record<string, string>>>(
    (acc, language) => {
        const moduleKey = `./locale/${language}.json`;
        const module = localeModules[moduleKey] as { default?: Record<string, string> } | undefined;
        acc[language] = module?.default ?? {};
        return acc;
    },
    {} as Record<Language, Record<string, string>>,
);

const resolveInitialLanguage = (): Language => {
    if (typeof window === 'undefined') {
        return 'en';
    }

    const stored = window.localStorage.getItem(storageKey);
    if (stored && supportedLanguages.includes(stored as Language)) {
        return stored as Language;
    }

    const browserLanguage = window.navigator.language.toLowerCase();
    const matched = supportedLanguages.find((lang) =>
        browserLanguage.startsWith(lang),
    );
    return matched ?? 'en';
};

const interpolate = (template: string, variables?: TranslationVariables) => {
    if (!variables) return template;
    return template.replace(/\{(\w+)\}/g, (match, key) => {
        const value = variables[key];
        return value !== undefined ? String(value) : match;
    });
};

const i18nInstance = i18next.createInstance();
const resources = supportedLanguages.reduce<Record<string, { translation: Record<string, string> }>>(
    (acc, language) => {
        acc[language] = { translation: translations[language] ?? {} };
        return acc;
    },
    {},
);

i18nInstance.use(initReactI18next).init({
    resources,
    lng: resolveInitialLanguage(),
    fallbackLng: 'en',
    supportedLngs: supportedLanguages,
    interpolation: {
        escapeValue: false,
        prefix: '{',
        suffix: '}',
    },
});

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [language, setLanguageState] = useState<Language>(() =>
        resolveInitialLanguage(),
    );

    const setLanguage = useCallback((value: Language) => {
        void i18nInstance.changeLanguage(value);
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(storageKey, value);
        }
        setLanguageState(value);
    }, []);

    const t = useCallback(
        (key: string, variables?: TranslationVariables) => {
            const translated = i18nInstance.t(key, variables);
            if (translated === key) {
                const fallback =
                    translations[language][key] ??
                    translations.en[key] ??
                    key;
                return interpolate(fallback, variables);
            }
            return translated;
        },
        [language],
    );

    const languageOptions = useMemo(
        () =>
            supportedLanguages.map((value) => ({
                value,
                label: t(languageLabels[value]),
            })),
        [t],
    );

    const value = useMemo(
        () => ({ language, setLanguage, t, languageOptions }),
        [language, setLanguage, t, languageOptions],
    );

    return (
        <I18nextProvider i18n={i18nInstance}>
            <LanguageContext.Provider value={value}>
                {children}
            </LanguageContext.Provider>
        </I18nextProvider>
    );
}

export function useTranslations() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useTranslations must be used within LanguageProvider');
    }
    return context;
}
